import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Box, Icon, ScoreBar, Text, Tooltip } from '@ksyos/design-system';
import type { ObesiQQuestionnaireName } from '@common/customControls/bariatrics';
import type { IProcessQuestionnaireResponse } from '@common/interfaces/api/processQuestionnaireResponsesApi';
import { obesiQThresholds, obesiQTranslations } from '@common/customControls/bariatrics';

interface IObesiQScoreBarProps {
    processQuestionnaireResponse: IProcessQuestionnaireResponse;
    questionnaireName: ObesiQQuestionnaireName;
}

export function ObesiQScoreBar(props: IObesiQScoreBarProps): JSX.Element {
    const { processQuestionnaireResponse, questionnaireName } = props;
    const raschTransformedScore = Number(
        processQuestionnaireResponse.items?.find((item) => item.text === 'raschTransformedScore')?.answer.value || 0
    );
    const threshold = obesiQThresholds[questionnaireName];

    return (
        <>
            <Box
                display="flex"
                flexDirection={['column', 'row']}
                gap={8}
            >
                <Box flexBasis={['auto', '250px']}>
                    <Box
                        alignItems="center"
                        display="flex"
                        gap={2}
                        sx={{
                            mb: '3px', // optical alignment
                        }}
                    >
                        <Text fontWeight="medium">{obesiQTranslations[questionnaireName].title}</Text>
                        <Tooltip content="Dit is de omgerekende score van de individuele antwoorden op de vragenlijst. Hoe beter het gaat met de patiënt, hoe hoger de score. De grenswaarde is de gemiddelde score van de Nederlandse bevolking.">
                            <Icon
                                color="primary"
                                icon="information"
                            />
                        </Tooltip>
                    </Box>

                    <Text fontSize="sm">{`Ingevuld op ${DateTime.from(
                        processQuestionnaireResponse.performedDateTime
                    ).format('DD MMMM YYYY')}`}</Text>
                </Box>

                <Box
                    alignItems="center"
                    display="flex"
                    flex={1}
                    flexDirection="column"
                    gap={2}
                    maxWidth={400}
                >
                    <ScoreBar
                        maxValue={100}
                        minValue={0}
                        range={['extreme', 'none']}
                        rangeStops={[threshold]}
                        value={raschTransformedScore}
                        valueRange={raschTransformedScore < threshold ? 'extreme' : 'none'}
                    />
                    <Text fontSize="sm">Grenswaarde {threshold.toLocaleString('nl-NL')}</Text>
                </Box>
            </Box>
        </>
    );
}
